/**
 * AdminOrdersEnhanced - Enhanced Orders list view with standardized table
 * 
 * Features:
 * - Server-side sorting on all columns
 * - Server-side filtering (status, payment status, date range, amount range)
 * - Global search
 * - Multi-select with bulk actions
 * - Admin-only delete with confirmation
 * - Role-based actions
 * - Quick View drawer for order details
 */
import React, { useState, useCallback, useEffect } from 'react';
import { useAdminTable } from '../../hooks/useAdminTable';
import AdminDataTable, {
  MonoCell,
  CurrencyCell,
  DateCell,
  StatusCell,
  BulkActionToolbar,
} from '../../components/admin/AdminDataTable';
import AdminListHeader from '../../components/admin/AdminListHeader';
import {
  FilterBar,
  GlobalSearch,
  StatusFilter,
  DateRangeFilter,
  NumericRangeFilter,
  ActiveFilterChips,
} from '../../components/admin/AdminTableFilters';
import {
  DeleteConfirmationModal,
  BulkStatusUpdateModal,
} from '../../components/admin/BulkActionModals';
import OrderQuickView from '../../components/admin/OrderQuickView';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { adminAPI, orderLifecycleAPI } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import {
  Eye,
  FileText,
  Trash2,
  RefreshCw,
  ShoppingCart,
  CheckCircle,
  Clock,
  Package,
  Truck,
  XCircle,
  AlertTriangle,
  ChevronDown,
  Calendar,
  AlertCircle,
  Bell,
  MapPin,
  Printer,
  Mail,
  ExternalLink,
  Layers,
} from 'lucide-react';

// ===================================================
// EXPANDED ROW COMPONENT
// ===================================================

/**
 * ExpandedOrderRow - Shows line items, summary, and quick actions when row is expanded
 */
const ExpandedOrderRow = ({ order, onViewFullDetails, onPrintInvoice, onSendEmail }) => {
  const items = order.items || [];
  const subtotal = order.subtotal || 0;
  const tax = order.tax || 0;
  const shipping = order.shipping_cost || 0;
  const discount = order.discount || 0;
  const total = order.total || 0;
  
  // Ship-to info
  const shipTo = order.ship_to_snapshot || order.shipping_address_snapshot;
  
  return (
    <div className="grid grid-cols-12 gap-6">
      {/* Line Items - 6 columns */}
      <div className="col-span-6">
        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Line Items</h4>
        <div className="space-y-1.5 max-h-40 overflow-y-auto">
          {items.length > 0 ? items.map((item, idx) => (
            <div key={idx} className="flex items-center justify-between text-sm bg-white rounded px-2 py-1.5 border border-gray-100">
              <div className="flex-1 min-w-0">
                <span className="font-medium text-gray-800 truncate block">
                  {item.product_name || item.description || 'Item'}
                </span>
                {item.description && item.product_name && (
                  <span className="text-xs text-gray-500 truncate block">{item.description}</span>
                )}
              </div>
              <div className="flex items-center gap-4 ml-4 flex-shrink-0">
                <span className="text-gray-500 text-xs">{item.quantity} × ${(item.unit_price || 0).toFixed(2)}</span>
                <span className="font-medium w-20 text-right">${((item.quantity || 0) * (item.unit_price || 0)).toFixed(2)}</span>
              </div>
            </div>
          )) : (
            <p className="text-sm text-gray-400 italic">No line items</p>
          )}
        </div>
      </div>
      
      {/* Summary - 3 columns */}
      <div className="col-span-3">
        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Summary</h4>
        <div className="bg-white rounded border border-gray-100 p-3 space-y-1.5 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-500">Subtotal</span>
            <span>${subtotal.toFixed(2)}</span>
          </div>
          {tax > 0 && (
            <div className="flex justify-between">
              <span className="text-gray-500">Tax</span>
              <span>${tax.toFixed(2)}</span>
            </div>
          )}
          {shipping > 0 && (
            <div className="flex justify-between">
              <span className="text-gray-500">Shipping</span>
              <span>${shipping.toFixed(2)}</span>
            </div>
          )}
          {discount > 0 && (
            <div className="flex justify-between text-green-600">
              <span>Discount</span>
              <span>-${discount.toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between font-semibold border-t pt-1.5 mt-1.5">
            <span>Total</span>
            <span>${total.toFixed(2)}</span>
          </div>
        </div>
        
        {/* Ship-to snippet */}
        {shipTo && (
          <div className="mt-3">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 flex items-center gap-1">
              <MapPin className="w-3 h-3" /> Ship To
            </h4>
            <p className="text-xs text-gray-600 leading-relaxed">
              {shipTo.contact_name || shipTo.full_name}<br />
              {shipTo.address?.address_line1 || shipTo.address_line1}<br />
              {shipTo.address?.city || shipTo.city}, {shipTo.address?.state || shipTo.state} {shipTo.address?.zip_code || shipTo.zip_code}
            </p>
          </div>
        )}
      </div>
      
      {/* Quick Actions - 3 columns */}
      <div className="col-span-3">
        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Quick Actions</h4>
        <div className="space-y-2">
          <Button
            variant="outline"
            size="sm"
            className="w-full justify-start text-sm"
            onClick={() => onViewFullDetails?.(order.order_id)}
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            Open Full Details
          </Button>
          {order.invoice_number && (
            <Button
              variant="outline"
              size="sm"
              className="w-full justify-start text-sm"
              onClick={() => onPrintInvoice?.(order.order_id)}
            >
              <Printer className="w-4 h-4 mr-2" />
              Print Invoice
            </Button>
          )}
          {order.customer_email && (
            <Button
              variant="outline"
              size="sm"
              className="w-full justify-start text-sm"
              onClick={() => onSendEmail?.(order.order_id)}
            >
              <Mail className="w-4 h-4 mr-2" />
              Send Email
            </Button>
          )}
        </div>
        
        {/* Order notes preview */}
        {order.order_summary && (
          <div className="mt-3">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Notes</h4>
            <p className="text-xs text-gray-600 bg-white rounded border border-gray-100 p-2 line-clamp-2">
              {order.order_summary}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// ===================================================
// STATUS CONFIGURATION
// ===================================================

// P1 Minimal Status Sets
const OPS_STATUSES = [
  { value: 'OPEN', label: 'Open', color: 'bg-blue-100 text-blue-700' },
  { value: 'IN_PROGRESS', label: 'In Progress', color: 'bg-purple-100 text-purple-700' },
  { value: 'COMPLETED', label: 'Completed', color: 'bg-green-100 text-green-700', icon: CheckCircle },
];

const INVOICE_STATUSES = [
  { value: 'NONE', label: 'Not Issued', color: 'bg-gray-100 text-gray-500' },
  { value: 'NOT_ISSUED', label: 'Not Issued', color: 'bg-gray-100 text-gray-500' },
  { value: 'ISSUED', label: 'Issued', color: 'bg-blue-100 text-blue-700' },
  { value: 'SENT', label: 'Sent', color: 'bg-indigo-100 text-indigo-700' },
  { value: 'PAID', label: 'Paid', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  { value: 'VOID', label: 'Void', color: 'bg-red-100 text-red-700', icon: XCircle },
];

const FULFILLMENT_STATUSES = [
  { value: 'NONE', label: 'None', color: 'bg-gray-100 text-gray-500' },
  { value: 'READY', label: 'Ready', color: 'bg-cyan-100 text-cyan-700' },
  { value: 'SHIPPED', label: 'Shipped', color: 'bg-blue-100 text-blue-700', icon: Truck },
];

// Legacy statuses (kept for backwards compatibility)
const ORDER_STATUSES = [
  { value: 'new', label: 'New', color: 'bg-blue-100 text-blue-700' },
  { value: 'pending', label: 'Pending', color: 'bg-gray-100 text-gray-700' },
  { value: 'waiting_artwork', label: 'Waiting Artwork', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'proof_sent', label: 'Proof Sent', color: 'bg-indigo-100 text-indigo-700' },
  { value: 'approved', label: 'Approved', color: 'bg-teal-100 text-teal-700' },
  { value: 'in_production', label: 'In Production', color: 'bg-purple-100 text-purple-700' },
  { value: 'ready', label: 'Ready', color: 'bg-cyan-100 text-cyan-700' },
  { value: 'shipped', label: 'Shipped', color: 'bg-blue-100 text-blue-700', icon: Truck },
  { value: 'closed', label: 'Closed', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-red-100 text-red-700', icon: XCircle },
];

const PAYMENT_STATUSES = [
  { value: 'unpaid', label: 'Unpaid', color: 'bg-orange-100 text-orange-700' },
  { value: 'partial', label: 'Partial', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'paid', label: 'Paid', color: 'bg-green-100 text-green-700' },
];

// Convert to lookup objects for StatusCell
const OPS_STATUS_CONFIG = OPS_STATUSES.reduce((acc, s) => { acc[s.value] = s; return acc; }, {});
const INVOICE_STATUS_CONFIG = INVOICE_STATUSES.reduce((acc, s) => { acc[s.value] = s; return acc; }, {});
const FULFILLMENT_STATUS_CONFIG = FULFILLMENT_STATUSES.reduce((acc, s) => { acc[s.value] = s; return acc; }, {});
const STATUS_CONFIG = ORDER_STATUSES.reduce((acc, s) => { acc[s.value] = s; return acc; }, {});
const PAYMENT_STATUS_CONFIG = PAYMENT_STATUSES.reduce((acc, s) => { acc[s.value] = s; return acc; }, {});

// ===================================================
// DUE DATE COMPONENT
// ===================================================

/**
 * DueDateCell - Shows due date with color-coded badge based on urgency
 * 🔴 Overdue (due date < today and not COMPLETED)
 * 🟡 Due soon (within 7 days and not COMPLETED)
 * Neutral otherwise
 */
const DueDateCell = ({ dueAt, opsStatus }) => {
  if (!dueAt) {
    return <span className="text-gray-400 text-xs">—</span>;
  }
  
  const dueDate = new Date(dueAt);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dueDateNormalized = new Date(dueDate);
  dueDateNormalized.setHours(0, 0, 0, 0);
  
  const diffDays = Math.ceil((dueDateNormalized - today) / (1000 * 60 * 60 * 24));
  const isCompleted = opsStatus === 'COMPLETED';
  
  // Format date as MMM DD (e.g., Jan 15)
  const formattedDate = dueDate.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric' 
  });
  
  // Determine badge style
  let badgeClass = 'bg-gray-100 text-gray-600'; // neutral
  let icon = null;
  
  if (!isCompleted) {
    if (diffDays < 0) {
      // Overdue
      badgeClass = 'bg-red-100 text-red-700';
      icon = <AlertCircle className="w-3 h-3" />;
    } else if (diffDays <= 7) {
      // Due soon (within 7 days)
      badgeClass = 'bg-amber-100 text-amber-700';
      icon = <Clock className="w-3 h-3" />;
    }
  }
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${badgeClass}`}>
      {icon}
      {formattedDate}
    </span>
  );
};

// ===================================================
// QUICK EDIT STATUS COMPONENT
// ===================================================

/**
 * QuickEditStatus - Inline dropdown for quick status changes
 */
const QuickEditStatus = ({ orderId, currentStatus, statusOptions, statusConfig, onUpdate, type }) => {
  const [loading, setLoading] = useState(false);
  const config = statusConfig[currentStatus] || statusConfig.NONE || { label: currentStatus, color: 'bg-gray-100 text-gray-700' };
  const Icon = config.icon;

  const handleStatusChange = async (newStatus) => {
    if (newStatus === currentStatus) return;
    
    setLoading(true);
    try {
      await onUpdate(orderId, newStatus);
      toast.success(`${type} status updated to ${newStatus}`);
    } catch (err) {
      toast.error(`Failed to update ${type} status: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          disabled={loading}
          className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-all cursor-pointer hover:opacity-80 ${config.color} ${loading ? 'opacity-50' : ''}`}
          data-testid={`quick-edit-${type.toLowerCase()}-${orderId}`}
        >
          {Icon && <Icon className="w-3 h-3" />}
          {config.label}
          <ChevronDown className="w-3 h-3 ml-0.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-36">
        {statusOptions.map((option) => (
          <DropdownMenuItem
            key={option.value}
            onClick={() => handleStatusChange(option.value)}
            className={`flex items-center gap-2 ${option.value === currentStatus ? 'bg-gray-100' : ''}`}
          >
            <span className={`w-2 h-2 rounded-full ${option.color.split(' ')[0]}`} />
            {option.label}
            {option.value === currentStatus && <CheckCircle className="w-3 h-3 ml-auto text-green-600" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// ===================================================
// MAIN COMPONENT
// ===================================================

/**
 * AdminOrdersEnhanced - Enhanced Orders list view
 * 
 * @param {function} onViewOrder - Callback when viewing an order
 * @param {function} onCreateOrder - Callback to open create order modal
 * @param {string} filterMode - Optional filter mode: 'quote' | 'invoiced' | null (default: all orders)
 * @param {string} initialAccountId - Optional account ID to filter orders by customer
 * @param {string} initialContactId - Optional contact ID to filter orders by contact
 * @param {string} initialLeadId - Optional lead ID to filter orders by lead
 */
const AdminOrdersEnhanced = ({ 
  onViewOrder, 
  onCreateOrder,
  onCombineOrders, // NEW: Callback to combine selected orders into new order
  filterMode = null, 
  initialAccountId = null,
  initialContactId = null,
  initialLeadId = null
}) => {
  const { isAdmin } = useAuth();
  
  // Bulk action modals
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  
  // Quick View state
  const [quickViewOrderId, setQuickViewOrderId] = useState(null);
  const [quickViewOpen, setQuickViewOpen] = useState(false);
  
  // Dynamic filter options state
  const [dynamicStatusOptions, setDynamicStatusOptions] = useState([]);
  const [dynamicPaymentOptions, setDynamicPaymentOptions] = useState([]);
  const [filterOptionsLoading, setFilterOptionsLoading] = useState(true);
  
  // Order metrics state
  const [metrics, setMetrics] = useState({
    open_orders: 0,
    in_progress: 0,
    overdue_invoices: 0
  });
  const [metricsLoading, setMetricsLoading] = useState(true);
  
  // Fetch order metrics
  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        const data = await adminAPI.getOrderMetrics();
        setMetrics(data);
      } catch (err) {
        console.error('Failed to fetch order metrics:', err);
      } finally {
        setMetricsLoading(false);
      }
    };
    
    fetchMetrics();
  }, []);
  
  // Fetch filter options on mount
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const options = await adminAPI.getOrderFilterOptions();
        
        // Build status options from ops_statuses (the current status field used in UI)
        // Map to format: { value, label, color }
        const statusColorMap = {
          'OPEN': 'bg-blue-100 text-blue-700',
          'IN_PROGRESS': 'bg-yellow-100 text-yellow-700',
          'COMPLETED': 'bg-green-100 text-green-700',
          'CANCELLED': 'bg-red-100 text-red-700',
          'ON_HOLD': 'bg-orange-100 text-orange-700',
          'PENDING': 'bg-gray-100 text-gray-700',
        };
        
        const statusOptions = (options.ops_statuses || []).map(status => ({
          value: status,
          label: status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
          color: statusColorMap[status] || 'bg-gray-100 text-gray-600'
        }));
        setDynamicStatusOptions(statusOptions);
        
        // Build payment status options
        const paymentColorMap = {
          'unpaid': 'bg-orange-100 text-orange-700',
          'pending': 'bg-yellow-100 text-yellow-700',
          'partial': 'bg-amber-100 text-amber-700',
          'paid': 'bg-green-100 text-green-700',
        };
        
        const paymentOptions = (options.payment_statuses || []).map(status => ({
          value: status,
          label: status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
          color: paymentColorMap[status] || 'bg-gray-100 text-gray-600'
        }));
        setDynamicPaymentOptions(paymentOptions);
        
      } catch (err) {
        console.error('Failed to fetch filter options:', err);
        // Fall back to empty arrays - filter will show "All" only
      } finally {
        setFilterOptionsLoading(false);
      }
    };
    
    fetchFilterOptions();
  }, []);
  
  // Fetch function for orders - applies filterMode and parent filters
  const fetchOrders = useCallback(async (params) => {
    // Apply filter mode to params
    const adjustedParams = { ...params };
    
    if (filterMode === 'quote') {
      // Orders with quote_status set (not null, not empty)
      adjustedParams.has_quote = 'true';
    } else if (filterMode === 'invoiced') {
      // Orders with invoice_status set
      adjustedParams.has_invoice = 'true';
    }
    
    // Apply parent filters (from "View All" on detail pages)
    if (initialAccountId) {
      adjustedParams.account_id = initialAccountId;
    }
    if (initialContactId) {
      adjustedParams.contact_id = initialContactId;
    }
    if (initialLeadId) {
      adjustedParams.lead_id = initialLeadId;
    }
    
    return await adminAPI.getOrders(adjustedParams);
  }, [filterMode, initialAccountId, initialContactId, initialLeadId]);
  
  // Use admin table hook
  const {
    data: orders,
    total,
    loading,
    error,
    page,
    pageSize,
    setPage,
    setPageSize,
    sortBy,
    sortDir,
    handleSort,
    search,
    setSearch,
    filters,
    setFilter,
    clearFilters,
    hasActiveFilters,
    selectedIds,
    setSelectedIds,
    clearSelection,
    selectedCount,
    density,
    setDensity,
    refresh,
  } = useAdminTable({
    fetchFn: fetchOrders,
    defaultSortBy: 'created_at',
    defaultSortDir: 'desc',
    defaultPageSize: 25,
    filterKeys: ['status', 'payment_status', 'due_filter', 'created_from', 'created_to', 'total_min', 'total_max', 'account_id'],
    initialFilters: initialAccountId ? { account_id: initialAccountId } : {},
    transformResponse: (data) => ({
      items: data.orders || data.items || [],
      total: data.total || 0
    })
  });
  
  // Quick Edit handlers - must be after useAdminTable to access refresh
  const handleQuickOpsStatusUpdate = useCallback(async (orderId, newStatus) => {
    await orderLifecycleAPI.updateOpsStatus(orderId, newStatus);
    refresh(); // Refresh the table
  }, [refresh]);
  
  const handleQuickFulfillmentUpdate = useCallback(async (orderId, newStatus) => {
    await orderLifecycleAPI.updateFulfillmentStatus(orderId, newStatus);
    refresh(); // Refresh the table
  }, [refresh]);
  
  // Get selected orders for validation
  const selectedOrders = orders.filter(o => selectedIds.includes(o.order_id || o.id));
  
  // Calculate blocked items for delete
  const getBlockedDeleteItems = () => {
    return selectedOrders.filter(o => {
      const status = (o.status || '').toLowerCase();
      const payStatus = (o.payment_status || '').toLowerCase();
      if (['closed', 'shipped', 'delivered'].includes(status)) {
        return true;
      }
      if (payStatus === 'paid') {
        return true;
      }
      return false;
    }).map(o => ({
      id: o.order_id || o.id,
      reason: o.payment_status === 'paid' 
        ? `Order ${o.order_id} is paid` 
        : `Order ${o.order_id} status is ${o.status}`
    }));
  };
  
  // Handle bulk delete
  const handleBulkDelete = async (reason) => {
    setBulkActionLoading(true);
    try {
      const result = await adminAPI.bulkDeleteOrders({
        ids: selectedIds,
        reason
      });
      
      if (result.deleted_count > 0) {
        toast.success(`Deleted ${result.deleted_count} order(s)`);
      }
      if (result.blocked_items?.length > 0) {
        toast.warning(`${result.blocked_items.length} order(s) could not be deleted`);
      }
      
      setShowDeleteModal(false);
      clearSelection();
      refresh();
    } catch (err) {
      toast.error(err.message || 'Failed to delete orders');
    } finally {
      setBulkActionLoading(false);
    }
  };
  
  // Handle bulk status update
  const handleBulkStatusUpdate = async (newStatus, note) => {
    setBulkActionLoading(true);
    try {
      // Update each order (API may not have bulk status endpoint yet)
      for (const id of selectedIds) {
        await adminAPI.updateOrder(id, { status: newStatus, admin_notes: note });
      }
      
      toast.success(`Updated ${selectedIds.length} order(s) to ${newStatus}`);
      setShowStatusModal(false);
      clearSelection();
      refresh();
    } catch (err) {
      toast.error(err.message || 'Failed to update orders');
    } finally {
      setBulkActionLoading(false);
    }
  };
  
  // Table columns definition - dynamically add quote/invoice columns based on filterMode
  const baseColumns = [
    {
      key: 'order_id',
      header: 'Order #',
      sortable: true,
      sortKey: 'order_id',
      render: (value, row) => (
        <MonoCell 
          value={value} 
          onClick={() => onViewOrder?.(value)}
          className="font-semibold"
        />
      )
    },
    {
      key: 'customer_name',
      header: 'Customer',
      sortable: true,
      render: (value, row) => (
        <div>
          <p className="font-medium text-ivory-800 truncate max-w-[200px]" title={value}>
            {value || '—'}
          </p>
          <p className="text-xs text-ivory-500 truncate max-w-[200px]" title={row.customer_email}>
            {row.customer_email || ''}
          </p>
        </div>
      )
    },
  ];
  
  // Quote-specific columns
  const quoteColumns = filterMode === 'quote' ? [
    {
      key: 'quote_number',
      header: 'Quote #',
      sortable: false,
      render: (value) => (
        <MonoCell value={value || '—'} className="text-sm" />
      )
    },
    {
      key: 'quote_status',
      header: 'Quote Status',
      sortable: false,
      render: (value) => {
        const config = {
          draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700' },
          issued: { label: 'Issued', color: 'bg-blue-100 text-blue-700' },
          sent: { label: 'Sent', color: 'bg-indigo-100 text-indigo-700' },
          approved: { label: 'Approved', color: 'bg-green-100 text-green-700' },
          rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700' },
          expired: { label: 'Expired', color: 'bg-orange-100 text-orange-700' },
        };
        return <StatusCell value={value} statusConfig={config} />;
      }
    },
  ] : [];
  
  // Invoice-specific columns
  const invoiceColumns = filterMode === 'invoiced' ? [
    {
      key: 'invoice_number',
      header: 'Invoice #',
      sortable: false,
      render: (value) => (
        <MonoCell value={value || '—'} className="text-sm" />
      )
    },
    {
      key: 'invoice_status',
      header: 'Invoice Status',
      sortable: false,
      render: (value) => <StatusCell value={value || 'NOT_ISSUED'} statusConfig={INVOICE_STATUS_CONFIG} />
    },
  ] : [];
  
  // Standard columns - P1 shows 3 independent status tracks with Quick Edit
  const standardColumns = [
    {
      key: 'ops_status',
      header: 'Ops',
      sortable: false,
      render: (value, row) => (
        <QuickEditStatus
          orderId={row.order_id || row.id}
          currentStatus={value || 'OPEN'}
          statusOptions={OPS_STATUSES}
          statusConfig={OPS_STATUS_CONFIG}
          onUpdate={handleQuickOpsStatusUpdate}
          type="Ops"
        />
      )
    },
    {
      key: 'invoice_status',
      header: 'Invoice',
      sortable: false,
      render: (value) => <StatusCell value={value || 'NOT_ISSUED'} statusConfig={INVOICE_STATUS_CONFIG} />
    },
    {
      key: 'reminders',
      header: 'Reminders',
      sortable: false,
      render: (_, row) => {
        // Only show for orders with issued invoices
        if (!row.invoice_number) return <span className="text-gray-300">—</span>;
        
        const schedule = row.invoice_snapshot?.reminder_schedule;
        const beforeCount = schedule?.before_due?.length || 0;
        const afterCount = schedule?.after_due?.length || 0;
        const totalReminders = beforeCount + afterCount;
        
        if (totalReminders === 0) {
          return <span className="text-gray-400 text-xs">None set</span>;
        }
        
        return (
          <div className="flex items-center gap-1">
            <span 
              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700"
              title={`${beforeCount} before due, ${afterCount} after due`}
            >
              <Bell className="w-3 h-3 mr-1" />
              {totalReminders}
            </span>
          </div>
        );
      }
    },
    {
      key: 'fulfillment_status',
      header: 'Fulfillment',
      sortable: false,
      render: (value, row) => (
        <QuickEditStatus
          orderId={row.order_id || row.id}
          currentStatus={value || 'NONE'}
          statusOptions={FULFILLMENT_STATUSES}
          statusConfig={FULFILLMENT_STATUS_CONFIG}
          onUpdate={handleQuickFulfillmentUpdate}
          type="Fulfillment"
        />
      )
    },
    {
      key: 'due_at',
      header: 'Due',
      sortable: true,
      render: (value, row) => (
        <DueDateCell dueAt={value} opsStatus={row.ops_status} />
      )
    },
    {
      key: 'total',
      header: 'Total',
      sortable: true,
      align: 'right',
      render: (value) => <CurrencyCell value={value} />
    },
    {
      key: 'created_at',
      header: 'Created',
      sortable: true,
      render: (value) => <DateCell value={value} />
    }
    // Actions column removed - users can expand rows or use bulk actions
  ];
  
  // Combine columns based on filter mode
  const columns = [...baseColumns, ...quoteColumns, ...invoiceColumns, ...standardColumns];
  
  // Get title based on filter mode
  const getTitle = () => {
    if (filterMode === 'quote') return 'Orders in Quote Mode';
    if (filterMode === 'invoiced') return 'Orders with Invoice Issued';
    return 'Orders';
  };
  
  // Filter labels for chips
  const filterLabels = {
    status: 'Status',
    payment_status: 'Payment',
    due_filter: 'Due Date',
    created_from: 'From',
    created_to: 'To',
    total_min: 'Min Amount',
    total_max: 'Max Amount',
  };
  
  return (
    <div className="space-y-6" data-testid="admin-orders-enhanced">
      {/* Header */}
      <AdminListHeader
        title={getTitle()}
        subtitle={total > 0 ? `${total.toLocaleString()} total` : undefined}
        onRefresh={refresh}
        refreshing={loading}
        density={density}
        onDensityChange={setDensity}
        createLabel="Create Order"
        onCreate={onCreateOrder}
        canCreate={isAdmin}
      />
      
      {/* Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="order-metrics-cards">
        {/* Open Orders Card */}
        <div 
          className="bg-blue-50 rounded-lg border border-blue-200 p-4 cursor-pointer hover:border-blue-400 transition-colors"
          onClick={() => setFilter('status', 'OPEN')}
          data-testid="metric-open-orders"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <ShoppingCart className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-xs text-blue-600 font-medium uppercase tracking-wide">Open Orders</p>
              <p className="text-2xl font-bold text-blue-700">
                {metricsLoading ? '...' : metrics.open_orders}
              </p>
            </div>
          </div>
        </div>
        
        {/* In-Progress Card */}
        <div 
          className="bg-amber-50 rounded-lg border border-amber-200 p-4 cursor-pointer hover:border-amber-400 transition-colors"
          onClick={() => setFilter('status', 'IN_PROGRESS')}
          data-testid="metric-in-progress"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-xs text-amber-600 font-medium uppercase tracking-wide">In Progress</p>
              <p className="text-2xl font-bold text-amber-700">
                {metricsLoading ? '...' : metrics.in_progress}
              </p>
            </div>
          </div>
        </div>
        
        {/* Overdue Invoices Card */}
        <div 
          className="bg-red-50 rounded-lg border border-red-200 p-4 cursor-pointer hover:border-red-400 transition-colors"
          onClick={() => setFilter('due_filter', 'overdue')}
          data-testid="metric-overdue-invoices"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <AlertCircle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-xs text-red-600 font-medium uppercase tracking-wide">Overdue Invoices</p>
              <p className="text-2xl font-bold text-red-700">
                {metricsLoading ? '...' : metrics.overdue_invoices}
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Filters */}
      <FilterBar hasActiveFilters={hasActiveFilters} onClearAll={clearFilters}>
        <GlobalSearch
          value={search}
          onChange={setSearch}
          placeholder="Search order #, customer name, email..."
          className="flex-1 min-w-[250px]"
        />
        
        <StatusFilter
          value={filters.status}
          onChange={(v) => setFilter('status', v)}
          options={dynamicStatusOptions}
          placeholder={filterOptionsLoading ? "Loading..." : "All Statuses"}
          className="w-[180px]"
          data-testid="order-status-filter"
          disabled={filterOptionsLoading}
        />
        
        <StatusFilter
          value={filters.payment_status}
          onChange={(v) => setFilter('payment_status', v)}
          options={dynamicPaymentOptions}
          placeholder={filterOptionsLoading ? "Loading..." : "All Payments"}
          className="w-[160px]"
          data-testid="order-payment-filter"
          disabled={filterOptionsLoading}
        />
        
        <StatusFilter
          value={filters.due_filter}
          onChange={(v) => setFilter('due_filter', v)}
          options={[
            { value: 'overdue', label: 'Overdue', color: 'bg-red-100 text-red-700' },
            { value: 'due_7days', label: 'Due in 7 days', color: 'bg-amber-100 text-amber-700' },
            { value: 'no_due', label: 'No due date', color: 'bg-gray-100 text-gray-500' },
          ]}
          placeholder="All Due Dates"
          className="w-[160px]"
          data-testid="order-due-filter"
        />
        
        <DateRangeFilter
          fromValue={filters.created_from}
          toValue={filters.created_to}
          onFromChange={(v) => setFilter('created_from', v)}
          onToChange={(v) => setFilter('created_to', v)}
          label="Date Range"
          className="w-[200px]"
        />
        
        <NumericRangeFilter
          minValue={filters.total_min}
          maxValue={filters.total_max}
          onMinChange={(v) => setFilter('total_min', v)}
          onMaxChange={(v) => setFilter('total_max', v)}
          label="Amount"
          prefix="$"
          className="w-[200px]"
        />
      </FilterBar>
      
      {/* Active filter chips */}
      <ActiveFilterChips
        filters={filters}
        filterLabels={filterLabels}
        onRemove={(key) => setFilter(key, null)}
        onClearAll={clearFilters}
      />
      
      {/* Bulk action toolbar */}
      <BulkActionToolbar
        selectedCount={selectedCount}
        onClearSelection={clearSelection}
      >
        {/* Re-Order button - appears when 1+ orders selected */}
        {onCombineOrders && selectedIds.length >= 1 && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onCombineOrders(selectedIds)}
            className="bg-amber-50 border-amber-300 text-amber-700 hover:bg-amber-100"
            data-testid="bulk-reorder-btn"
          >
            <Layers className="w-4 h-4 mr-2" />
            {selectedIds.length === 1 
              ? 'Re-Order' 
              : `Re-Order (Combine ${selectedIds.length})`
            }
          </Button>
        )}
        
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowStatusModal(true)}
          data-testid="bulk-status-btn"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Update Status
        </Button>
        
        {/* Delete button - Admin only */}
        {isAdmin && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowDeleteModal(true)}
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
            data-testid="bulk-delete-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete
          </Button>
        )}
      </BulkActionToolbar>
      
      {/* Error display */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" />
          {error}
        </div>
      )}
      
      {/* Data Table */}
      <AdminDataTable
        tableId="orders"
        data={orders}
        columns={columns}
        loading={loading}
        emptyMessage="No orders found"
        emptyIcon={ShoppingCart}
        
        // Selection
        selectable={true}
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        getRowId={(row) => row.order_id || row.id}
        
        // Expandable rows
        expandable={true}
        singleExpand={true}
        renderExpandedRow={(row) => (
          <ExpandedOrderRow
            order={row}
            onViewFullDetails={(orderId) => onViewOrder?.(orderId)}
            onPrintInvoice={(orderId) => {
              const url = orderLifecycleAPI.getInvoicePdfUrl(orderId);
              window.open(url, '_blank');
            }}
            onSendEmail={(orderId) => {
              // Open quick view for email actions
              setQuickViewOrderId(orderId);
              setQuickViewOpen(true);
            }}
          />
        )}
        
        // Sorting
        sortBy={sortBy}
        sortDir={sortDir}
        onSort={handleSort}
        
        // Pagination
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        
        // Density
        density={density}
        onDensityChange={setDensity}
        
        // Column preferences
        showColumnSelector={true}
        resizableColumns={true}
        
        // Row click navigates to order detail page (disabled when expandable)
        // onRowClick={(row) => onViewOrder?.(row.order_id || row.id)}
        
        data-testid="orders-data-table"
      />
      
      {/* Order Quick View Drawer - kept for action menu */}
      <OrderQuickView
        open={quickViewOpen}
        onOpenChange={setQuickViewOpen}
        orderId={quickViewOrderId}
        onViewFullDetails={(orderId) => onViewOrder?.(orderId)}
      />
      
      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        open={showDeleteModal}
        onOpenChange={setShowDeleteModal}
        onConfirm={handleBulkDelete}
        count={selectedCount}
        itemType="order"
        loading={bulkActionLoading}
        blockedItems={getBlockedDeleteItems()}
        warnings={[
          'Soft-deleted orders can be restored by an administrator.',
          'Related invoices and work orders will NOT be deleted.'
        ]}
        requireReason={true}
      />
      
      {/* Bulk Status Update Modal */}
      <BulkStatusUpdateModal
        open={showStatusModal}
        onOpenChange={setShowStatusModal}
        onConfirm={handleBulkStatusUpdate}
        count={selectedCount}
        itemType="order"
        statusOptions={ORDER_STATUSES.filter(s => !['closed', 'cancelled'].includes(s.value))}
        loading={bulkActionLoading}
      />
    </div>
  );
};

export default AdminOrdersEnhanced;
